import { useNavigate } from 'react-router';
import { queryConfig } from '../api/queryConfig';
import { DataTable, type Column } from './DataTable';
import { Pagination } from './Pagination';
import { TransactionFilters } from './TransactionFilters';
import { useTransactionFilters } from '../hooks/useTransactionFilters';
import { useFittedPageSize } from '../hooks/useFittedPageSize';
import { TransactionStatusBadge } from './TransactionStatusBadge';
import { formatKobo } from '../lib/money';
import { formatDateTime } from '../lib/date';
import { formatTransactionCategory } from '../constants/transaction';

export interface Transaction {
  uid: string;
  transaction_reference: string;
  reference: string;
  amount: number;
  balance: number;
  name: string;
  narration: string;
  created_at: string;
  updated_at: string;
  type: string;
  category: string;
  program_name: string;
  status: string;
}

const useTransactions = queryConfig({});

const columns: Column<Transaction>[] = [
  { key: 'uid', header: 'UID', className: 'font-mono text-xs text-gray-400', cell: (t) => `${t.uid.slice(0, 8)}…`, copyValue: (t) => t.uid },
  { key: 'name', header: 'Name', className: 'text-gray-900', cell: (t) => t.name, copyValue: (t) => t.name },
  { key: 'program', header: 'Program', cell: (t) => t.program_name, copyValue: (t) => t.program_name },
  { key: 'type', header: 'Type', cell: (t) => t.type },
  { key: 'category', header: 'Category', cell: (t) => formatTransactionCategory(t.category) },
  { key: 'amount', header: 'Amount', className: 'text-gray-900', cell: (t) => formatKobo(t.amount), copyValue: (t) => formatKobo(t.amount) },
  { key: 'balance', header: 'Balance', cell: (t) => formatKobo(t.balance), copyValue: (t) => formatKobo(t.balance) },
  { key: 'status', header: 'Status', cell: (t) => <TransactionStatusBadge status={t.status} /> },
  { key: 'date', header: 'Date', className: 'text-gray-500', sortable: true, cell: (t) => formatDateTime(t.updated_at || t.created_at) },
];

export const TransactionsTable = () => {
  const navigate = useNavigate();
  const { tableRef, pageSize } = useFittedPageSize();
  const {
    filters,
    query,
    page,
    goToPage,
    hasFilters,
    hasDateRange,
    canClear,
    setFilter,
    clearFilters,
    sortDirection,
    toggleSort,
    searchParams,
  } = useTransactionFilters();

  const { data, isLoading, isError } = useTransactions<{
    page: number;
    limit: number;
    total: number;
    transactions: Transaction[];
  }>({
    url: '/v1/ops/transactions',
    cacheKey: 'transactions',
    params: {
      page,
      limit: pageSize,
      sort: sortDirection,
      sort_on: 'updated_at',
      q: query || undefined,
      statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
      categories: filters.categories.length > 0 ? filters.categories : undefined,
      type: filters.type || undefined,
      program_uid: filters.program_uid || undefined,
      starts_at: hasDateRange ? new Date(`${filters.from}T00:00:00`).toISOString() : undefined,
      ends_at: hasDateRange ? new Date(`${filters.to}T23:59:59.999`).toISOString() : undefined,
    },
  });

  const transactions = data?.payload?.transactions ?? [];
  const total = data?.payload?.total ?? 0;
  const showPagination = !isLoading && !isError && transactions.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <TransactionFilters filters={filters} onChange={setFilter} onClear={clearFilters} canClear={canClear} />
      <div ref={tableRef}>
        <DataTable
          columns={columns}
          rows={transactions}
          rowKey={(t) => t.uid}
          sort={{ key: 'date', direction: sortDirection, onChange: toggleSort }}
          onRowClick={(t) => navigate(`/transactions/${t.uid}`, { state: { transaction: t, search: searchParams.toString() } })}
          isLoading={isLoading}
          isError={isError}
          errorLabel="Failed to load transactions."
          emptyLabel={hasFilters ? 'No transactions match these filters.' : 'No transactions found.'}
        />
      </div>
      {showPagination && (
        <Pagination page={page} pageSize={pageSize} total={total} onPageChange={goToPage} />
      )}
    </div>
  );
};
